import React from 'react';
import { useAppContext } from '../../../../../../Services/AppContext';
import {
	TermItem,
	useUpdateWorkplaceMutation,
	useWorkplaceInsuranceCompaniesQuery,
} from '../../../../../../graphql/graphql';
import Loading from '../../../../../../Components/Loading';
import styles from './ManagerInsuranceCompaniesContracted.module.scss';
import useTranslation from '../../../../../../Hooks/useTranslation';
import { formatInsuranceCompaniesOptions } from '../../../../Helpers/InsuranceCompanies/formatInsuranceCompaniesOptions';
import { getWorkplaceInsuranceCompanies } from '../../../../Helpers/InsuranceCompanies/getWorkplaceInsuranceCompaniesIds';
import ManagerInsuranceCompaniesCheckboxes from '../ManagerInsuranceCompaniesCheckboxes/ManagerInsuranceCompaniesCheckboxes';
import useApiClient from '../../../../../Base/Hooks/useApiClient';
import useNotification from '../../../../../../Hooks/useNotification.hook';
import useInsuranceCompanies from '../../../../Hooks/useInsuranceCompanies';

interface Props {
	onSubmit: () => void;
}

const ManagerInsuranceCompaniesContracted = ({ onSubmit }: Props) => {
	const { workplaceId } = useAppContext();
	const { t } = useTranslation();
	const [updateWorkplace] = useUpdateWorkplaceMutation();
	const { displayGQLError } = useApiClient();
	const { showNotificationSuccess } = useNotification();

	const {
		data: workplaceInsuranceCompaniesResponse,
		isLoading: isLoadingWorkplaceInsuranceCompanies,
		refetch: refetchWorkplaceInsuranceCompanies,
	} = useWorkplaceInsuranceCompaniesQuery({ workplaceId });

	const {
		data: insuranceCompaniesResponse,
		isLoading: isLoadingInsuranceCompanies,
	} = useInsuranceCompanies();

	if (isLoadingWorkplaceInsuranceCompanies || isLoadingInsuranceCompanies) {
		return <Loading />;
	}

	const insuranceCompaniesData =
		insuranceCompaniesResponse?.termItems?.data?.filter(
			(item): item is TermItem => item !== null,
		);
	const insuranceCompanies = formatInsuranceCompaniesOptions(
		insuranceCompaniesData,
	);
	const workplaceInsuranceCompanies = getWorkplaceInsuranceCompanies(
		workplaceInsuranceCompaniesResponse,
	);

	const handleSubmit = async (codes: Record<string, boolean>) => {
		const insuranceCompanies = insuranceCompaniesData!
			.filter(
				insuranceCompany =>
					insuranceCompany.value2 && codes[insuranceCompany.value2],
			)
			.map(insuranceCompany => ({
				code: insuranceCompany.value2 || '',
				name: insuranceCompany.name || '',
				numberCode: insuranceCompany.value || '',
			}));

		const result = await updateWorkplace({
			where: { id: workplaceId },
			data: { insuranceCompanies },
		});

		if ('error' in result) {
			displayGQLError(result);
		} else {
			showNotificationSuccess({
				content:
					'component.caseType.contractedInsuranceCompaniesSuccessMessage',
			});
			refetchWorkplaceInsuranceCompanies();
			onSubmit();
		}
	};

	return (
		<div className={styles.wrapper}>
			<div className={styles.title}>
				{t('component.caseType.toContractedInsuranceCompaniesButton')}
			</div>
			<ManagerInsuranceCompaniesCheckboxes
				items={insuranceCompanies}
				selectedItems={workplaceInsuranceCompanies}
				onSubmit={handleSubmit}
			/>
		</div>
	);
};

export default ManagerInsuranceCompaniesContracted;
